import { createElement } from 'react';
import { PiUserLight, PiXLight } from 'react-icons/pi';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';
import {
  TaskStatus,
  UserKind,
  type Contact,
  type ContactAddress,
  type FunnelConversation,
  type Task,
} from 'src/api/generated';
import type { TagDropdownOption } from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import type { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import type { useAllPersonAndSystemUsers } from '../hooks/users/system-users';

export const getConversationStatusQuery = (funnelId?: number) => {
  if (!funnelId) return {};

  return createQuery()
    .equals('funnelId', funnelId)
    .orderByAsc('position')
    .build();
};

export const getConversationTaskSummary = (conversationTasks: Task[]) => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isOpenTask = (task: Task) =>
    task.status !== TaskStatus.COMPLETED &&
    task.status !== TaskStatus.CANCELLED;

  const openTaskCount = conversationTasks.filter(isOpenTask).length;

  const overdueTaskCount = conversationTasks.filter(
    (task) => task.dueDate && new Date(task.dueDate) < now && isOpenTask(task)
  ).length;

  const upcomingTaskCount = conversationTasks.filter(
    (task) =>
      task.dueDate &&
      new Date(task.dueDate) >= now &&
      new Date(task.dueDate) <= tomorrow &&
      isOpenTask(task)
  ).length;

  return { openTaskCount, overdueTaskCount, upcomingTaskCount };
};

export const getConversationName = ({
  addresses,
  contact,
  isLoading,
}: {
  addresses?: ContactAddress[];
  contact?: Contact;
  isLoading: boolean;
}) => {
  if (isLoading) return 'Cargando...';

  const firstAddress = addresses?.[0];

  return formatFullName(
    contact?.firstName,
    contact?.lastName,
    firstAddress?.address
  );
};

export const getConversationHeaderAvatarText = (conversationName: string) => {
  const trimmedConversationName = conversationName.trim();

  if (!trimmedConversationName) return '?';

  const [firstWord, secondWord] = trimmedConversationName
    .split(/\s+/)
    .filter(Boolean);

  if (secondWord) {
    return `${firstWord[0]}${secondWord[0]}`.toUpperCase();
  }

  return trimmedConversationName.slice(0, 2).toUpperCase();
};

export const getConversationHeaderCurrentStatusName = (
  statuses: { id: number; name: string }[],
  funnelStatusId?: number
) => statuses.find((status) => status.id === funnelStatusId)?.name;

export const getConversationHeaderCurrentUserName = ({
  t,
  userId,
  users,
}: {
  t: ReturnType<typeof useI18n>['t'];
  userId?: FunnelConversation['userId'];
  users: ReturnType<typeof useAllPersonAndSystemUsers>['users'];
}) => {
  const currentUser = users.find((user) => user.id === userId);

  if (!currentUser) {
    return t('communications.contactPanel.conversations.fields.unassigned');
  }

  return formatFullName(currentUser.firstName, currentUser.lastName);
};

export const buildConversationUserOptions = ({
  t,
  userId,
  users,
}: {
  t: ReturnType<typeof useI18n>['t'];
  userId?: FunnelConversation['userId'];
  users: ReturnType<typeof useAllPersonAndSystemUsers>['users'];
}) => {
  const personOptions: TagDropdownOption[] = users
    .filter((user) => user.kind !== UserKind.SYSTEM)
    .map((user) => ({
      id: user.id,
      content: formatFullName(user.firstName, user.lastName, user.email),
      icon: createElement(PiUserLight, {
        className: 'h-4 w-4 text-secondary-600',
      }),
      selected: user.id === userId,
    }));

  const systemOptions: TagDropdownOption[] = users
    .filter((user) => user.kind === UserKind.SYSTEM)
    .map((user) => ({
      id: user.id,
      content: formatFullName(user.firstName, user.lastName, user.email),
      icon: createElement(AiRobotOutlineIcon, {
        className: 'h-4 w-4 text-secondary-600',
      }),
      selected: user.id === userId,
    }));

  const unassignOption: TagDropdownOption = {
    id: 'unassign',
    content: t('communications.contactPanel.conversations.fields.unassigned'),
    icon: createElement(PiXLight, {
      className: 'absolute right-4 h-4 w-4 text-secondary-600',
    }),
    selected: !userId,
    separator: personOptions.length > 0 || systemOptions.length > 0,
  };

  if (systemOptions.length === 0) {
    return [
      { ...unassignOption, separator: personOptions.length > 0 },
      ...personOptions.map(({ icon: _icon, ...rest }) => rest),
    ];
  }

  if (personOptions.length > 0) {
    personOptions[personOptions.length - 1] = {
      ...personOptions[personOptions.length - 1],
      separator: true,
    };
  }

  return [unassignOption, ...personOptions, ...systemOptions];
};
